import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { BackdropAI } from "../../../../AI/BackdropAI/BackdropAI";
import EthiquinLoading from "../../../../../../src/assets/images/Ethiquin/loadingWhite.gif";
import EthiquinAvatar from "../../../../../../src/assets/images/Ethiquin/ethiquin_avatar.svg";
import {
  alpha,
  Box,
  CardMedia,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { useSnackbar } from "notistack";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { CheckIconCircle } from "../../../../Icon";
import { TypingText } from "../../../../AI/TypingText/TypingText";
import { generateAiInvestigtionPlan } from "../../../../../apis/apiAi";
import { useReportId } from "../../../../../context/report/ReportContext";

const suggestionPlan = [
  {
    deadline: "",
    name: "Recopilación de evidencia documental",
    party: [],
    todos: [
      "Identificar fuentes documentales relevantes",
      "Solicitar acceso a registros y archivos necesarios",
      "Clasificar y organizar la documentación recopilada",
      "Verificar autenticidad e integridad de los documentos",
    ],
    status: "pending",
    details:
      "La recopilación y análisis de la evidencia documental, como registros, documentos y correos electrónicos, es crucial para respaldar o refutar las alegaciones del caso. Esta actividad permitirá identificar patrones, inconsistencias y posibles pruebas que ayuden a esclarecer los hechos.",
  },
  {
    deadline: "",
    name: "Entrevista a Manito MacQueen",
    party: [],
    todos: [
      "Preparar guía de preguntas para la entrevista",
      "Coordinar fecha y medio para realizar la entrevista",
      "Registrar y documentar las respuestas obtenidas",
      "Analizar coherencia del testimonio con otras evidencias",
    ],
    status: "pending",
    details:
      "La entrevista a Manito MacQueen, quien ha sido señalado como la víctima del incidente, es fundamental para comprender su perspectiva y determinar los posibles daños o perjuicios que haya sufrido. Es importante evaluar la coherencia de la información proporcionada para contrastarla con otras fuentes.",
  },
  {
    deadline: "",
    name: "Identificación de partes involucradas",
    party: [],
    todos: [],
    status: "pending",
    details:
      "Esta actividad tiene como objetivo identificar a todas las personas y entidades que tuvieron algún grado de participación directa o indirecta en los hechos investigados, con el fin de delimitar responsabilidades y alcances del caso.",
  },
  {
    deadline: "",
    name: "Revisión de antecedentes del caso",
    party: [],
    todos: [
      "Recopilar información de casos o eventos previos relacionados",
      "Analizar decisiones y acciones tomadas anteriormente",
      "Identificar patrones recurrentes o situaciones similares",
    ],
    status: "pending",
    details:
      "La revisión de antecedentes permitirá contextualizar el caso mediante el análisis de eventos previos, decisiones pasadas y situaciones relacionadas que puedan influir en la interpretación de los hechos actuales.",
  },
  {
    deadline: "",
    name: "Análisis cronológico de los hechos",
    party: [],
    todos: [
      "Recolectar fechas y eventos clave del caso",
      "Ordenar los hechos en una línea de tiempo",
      "Identificar vacíos o inconsistencias temporales",
      "Validar la cronología con evidencia disponible",
    ],
    status: "pending",
    details:
      "Consiste en organizar los hechos de manera cronológica para comprender la secuencia de eventos, identificar posibles contradicciones y detectar momentos clave relevantes para la investigación.",
  },
  {
    deadline: "",
    name: "Entrevista a testigos clave",
    party: [],
    todos: [],
    status: "pending",
    details:
      "La realización de entrevistas a testigos clave permitirá obtener versiones adicionales de los hechos, contrastar la información existente y fortalecer la objetividad del análisis del caso.",
  },
  {
    deadline: "",
    name: "Evaluación de riesgos y consecuencias",
    party: [],
    todos: [
      "Identificar riesgos potenciales derivados del incidente",
      "Evaluar impacto en personas y en la institución",
      "Determinar escenarios de corto y largo plazo",
      "Proponer medidas de mitigación",
      "Documentar conclusiones del análisis de riesgos",
    ],
    status: "pending",
    details:
      "Esta actividad busca identificar los posibles riesgos, impactos y consecuencias derivados del incidente, tanto a nivel personal como institucional, para apoyar la toma de decisiones informadas.",
  },
  {
    deadline: "",
    name: "Análisis de inconsistencias detectadas",
    party: [],
    todos: [],
    status: "pending",
    details:
      "Se analizarán las inconsistencias encontradas entre testimonios, documentos y otras evidencias, con el fin de determinar su origen y relevancia dentro del proceso de investigación.",
  },
  {
    deadline: "",
    name: "Elaboración de informe preliminar",
    party: [],
    todos: [
      "Consolidar los hallazgos principales de la investigación",
      "Redactar el borrador del informe preliminar",
      "Revisar claridad y coherencia del documento",
      "Incorporar observaciones internas",
    ],
    status: "pending",
    details:
      "La elaboración del informe preliminar permitirá sintetizar los hallazgos iniciales, documentar avances y establecer una base para futuras conclusiones o acciones posteriores.",
  },
  {
    deadline: "",
    name: "Revisión y validación de conclusiones",
    party: [],
    todos: [],
    status: "pending",
    details:
      "Esta etapa final consiste en revisar y validar las conclusiones obtenidas, asegurando que estén sustentadas por la evidencia recopilada y alineadas con los objetivos del plan de actividades.",
  },
];

export const SuggestionPlanAI = ({
  t,
  open,
  onClose,
  alias,
  plans,
  setActivity,
  setTodos,
  setParty,
  setIsGenerating,
  setIsEditing,
}) => {
  const id = useReportId();

  const theme = useTheme();
  // useRefs
  const progressIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const completedPartsRef = useRef({}); // { [index]: { primary: bool, secondary: bool } }
  const abortControllerRef = useRef(null);
  // Initial States
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'done' | 'error'
  const { enqueueSnackbar } = useSnackbar();

  const [suggestions, setSuggestions] = useState([]);

  const [progress, setProgress] = useState(0);

  // Typing control: current index being typed (or null if everything is finished)
  const [typingIndex, setTypingIndex] = useState(null);
  // Control animation
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // ---- CACHING HELPERS (localStorage) ----
  const cacheKey = useMemo(
    () => `suggestion_cache_${alias ?? "default"}`,
    [alias]
  );

  const cacheTTL = 1000 * 60 * 60 * 24; // saved 24h

  const loadFromCache = useCallback(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || typeof parsed.data === "undefined") {
        localStorage.removeItem(cacheKey);
        return null;
      }
      if (Date.now() - parsed.ts > cacheTTL) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      return parsed;
    } catch (e) {
      console.warn("Error leyendo cache suggestions:", e);
      return null;
    }
  }, [cacheKey, cacheTTL]);

  const saveToCache = useCallback(
    (data) => {
      try {
        const payload = { ts: Date.now(), data, generated: true };
        localStorage.setItem(cacheKey, JSON.stringify(payload));
      } catch (e) {
        console.warn("Could not save cache suggestions:", e);
      }
    },
    [cacheKey]
  );

  useEffect(() => {
    if (!open) return;
    // if there are already suggestions in status (e.g., reopen), do nothing
    if (suggestions && suggestions.length > 0) return;
    // Attempt to load from cache; if it exists, display everything without animation
    const cached = loadFromCache();
    if (cached && Array.isArray(cached.data) && cached.data.length > 0) {
      setSuggestions(cached.data);
      setStatus("done");
      setProgress(100);
      setShouldAnimate(false);
      setTypingIndex(null);
      return;
    }
  }, [open, alias]);

  // open/close reset
  useEffect(() => {
    if (!open) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  }, [open]);

  // clean unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // When the suggestions are ready, start the animation from the first item.
  useEffect(() => {
    if (status === "done" && suggestions.length > 0) {
      // reset tracking
      completedPartsRef.current = {};

      if (shouldAnimate) {
        setTypingIndex(0);
      } else {
        setTypingIndex(null);
      }
    } else {
      setTypingIndex(null);
      completedPartsRef.current = {};
    }
  }, [status, suggestions.length, shouldAnimate]);

  // Mark a part (primary | secondary) as complete for a given index
  const markPartComplete = useCallback(
    (index, part) => {
      if (!completedPartsRef.current[index]) {
        completedPartsRef.current[index] = { primary: false, secondary: false };
      }
      completedPartsRef.current[index][part] = true;
      // If there is no secondary for this item, we consider it completed immediately.
      const hasDetails = !!(
        suggestions[index]?.details && suggestions[index].details.trim()
      );
      if (!hasDetails) {
        completedPartsRef.current[index].secondary = true;
      }

      const bothDone =
        completedPartsRef.current[index].primary &&
        completedPartsRef.current[index].secondary;

      if (bothDone) {
        const next = index + 1;
        delete completedPartsRef.current[index];

        if (next < suggestions.length) {
          setTypingIndex(next);
        } else {
          setTypingIndex(null);
        }
      }
    },
    [suggestions]
  );

  const handlePrimaryComplete = useCallback(
    (index) => markPartComplete(index, "primary"),
    [markPartComplete]
  );
  const handleSecondaryComplete = useCallback(
    (index) => markPartComplete(index, "secondary"),
    [markPartComplete]
  );

  // const handleGenerate = async () => {
  //   setIsGenerating(true);

  //   try {
  //     const plan = await generateAiInvestigtionPlan({
  //       report_id: id,
  //       investigation_alias: alias,
  //     });
  //     setSuggestions(plan);
  //     saveToCache(plan);
  //     setIsEditing(false);
  //   } catch (error) {
  //     console.error("Error generando contenido:", error);
  //   } finally {
  //     setIsGenerating(false);
  //   }
  // };

  /**
   * Ejemplo de llamada "real":
   * - Reemplaza la función `fetchSuggestions` con tu fetch/fetchClient real.
   * - Usamos AbortController para poder cancelar la petición si el usuario cierra.
   */
  const fetchSuggestions = async (signal) => {
    // === EJEMPLO SIMULADO ===
    return new Promise((resolve, reject) => {
      timeoutRef.current = setTimeout(() => {
        if (signal && signal.aborted) {
          reject(new DOMException("Aborted", "AbortError"));
          return;
        }
        resolve(suggestionPlan);
        timeoutRef.current = null;
      }, 2500);
    });
  };

  // Fetch suggestions
  const handleGetSuggestion = async () => {
    if (status === "loading") return;

    setShouldAnimate(true);
    setStatus("loading");
    setProgress(0);

    // Progreso gradual fake hasta 80%
    progressIntervalRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + 10, 90));
    }, 300);

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    try {
      const data = await fetchSuggestions(signal);
      // const data = await handleGenerate(); // llamada real
      const arr = Array.isArray(data) ? data : [];

      setTypingIndex(-1);
      setSuggestions(arr);
      saveToCache(arr);
      setStatus("done");
      // Completar progreso al final
      setProgress(100);
    } catch (error) {
      if (error.name === "AbortError") {
        setStatus("idle");
        console.error(error);
      } else {
        console.error("Error fetching suggestions: ", error);
        setStatus("error");
      }
    } finally {
      // Clear interval/abortcontroller
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  // function to normalize text (trim, multiple spaces, lowercase)
  const normalize = (str) =>
    (str || "").trim().replace(/\s+/g, " ").toLowerCase();

  // Memorize a Set with the questions already present (standardized)
  const existingPlansSet = useMemo(() => {
    const arr = Array.isArray(plans) ? plans : [];
    return new Set(arr.map((p) => normalize(p?.name)));
  }, [plans]);

  const applySuggestionToForm = (suggestion) => {
    const normalizedTodos = Array.isArray(suggestion.todos)
      ? suggestion.todos.map((todo) =>
          typeof todo === "string" ? { name: todo, completed: false } : todo
        )
      : [];

    setActivity((prev) => ({
      ...prev,
      name: suggestion.name ?? "",
      details: suggestion.details ?? "",
      status: suggestion.status ?? "pending",
      deadline: suggestion.deadline ?? "",
    }));

    setTodos(normalizedTodos);
    setParty(Array.isArray(suggestion.party) ? [...suggestion.party] : []);
  };

  const handleSelectSuggestion = (suggestion) => {
    applySuggestionToForm(suggestion);
    enqueueSnackbar(t("activityPlan.plan_applied"), {
      variant: "success",
    });
    onClose();
  };

  // helper para extraer texto plano de un JSX
  const extractPlainText = (node) => {
    if (node == null || node === true || node === false) return "";
    if (typeof node === "string" || typeof node === "number")
      return String(node);
    if (Array.isArray(node)) return node.map(extractPlainText).join("");
    if (React.isValidElement(node))
      return extractPlainText(node.props.children);
    return "";
  };

  const resetState = useCallback(() => {
    setSuggestions([]);
    setStatus("idle");
    setProgress(0);
    setTypingIndex(null);
    setShouldAnimate(false);
    completedPartsRef.current = {};

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return (
    <BackdropAI
      title={t("activityPlan.generate_with_ai")}
      open={open}
      onClose={onClose}
      onExited={resetState}
    >
      {status === "loading" ? (
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 64px)",
            flexFlow: "column",
          }}
        >
          <Box
            role="status"
            aria-live="polite"
            sx={{ width: "100%", textAlign: "center" }}
          >
            <CardMedia
              component="img"
              src={EthiquinLoading}
              alt={t("Loading Suggestions")}
              sx={{
                width: { xs: "60%", md: "40%" },
                height: "auto",
                mx: "auto",
                my: 2.5,
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
            {/* Progress bar */}
            <Box
              sx={{
                width: { xs: "90%", md: "70%" },
                paddingY: 1,
                mx: "auto",
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e3f2fd",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                  },
                }}
                aria-label={t("Loading suggestions")}
              />
            </Box>
          </Box>
        </Stack>
      ) : suggestions.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <CardMedia
            component="img"
            src={EthiquinAvatar}
            alt={"loading..."}
            sx={{
              border: `2px solid ${theme.palette.primary.dark}`,
              borderRadius: "50%",
              width: "20%",
              height: "auto",
              mx: "auto",
              userSelect: "none",
            }}
          />
          <Typography
            color={"warning.dark"}
            textAlign="justify"
            sx={{
              borderLeft: `5px solid ${theme.palette.warning.main}`,
              backgroundColor: alpha(theme.palette.warning.light, 0.1),
              boxShadow: `0 0 10px -4px ${theme.palette.warning.dark}`,
              borderRadius: 1,
              py: 2,
              px: 3,
              mx: 3,
              my: 5,
              fontSize: 15,
            }}
          >
            <strong>{t("legalNotice.legal_notice")}: </strong>
            <Typography
              fontWeight={500}
              dangerouslySetInnerHTML={{
                __html: t("legalNotice.AI_generated_questions"),
              }}
              component={"span"}
            />
          </Typography>
          <EGButton
            label={`${t("activityPlan.generate_with_ai_button")}`}
            color="primary"
            iconName="outline_ai"
            onClick={handleGetSuggestion}
          />
        </Box>
      ) : (
        // Suggestion list
        <Box sx={{ px: 2 }}>
          <Typography mt={1.5} variant="h6">
            {`${t("activityPlan.select_an_activity_plan")}:`}
          </Typography>

          <List>
            {suggestions.map((s, i) => {
              const suggestionPlan = s.name ?? "";
              const isAdded = existingPlansSet.has(normalize(suggestionPlan));
              const visible =
                typingIndex === null
                  ? true
                  : typingIndex === -1
                  ? false
                  : i <= typingIndex;

              const primaryText = `${suggestionPlan}`;
              const secondaryText = (
                <Stack>
                  <Typography fontWeight={600} component="span" fontSize={12.5}>
                    {`${t("activityPlan.activity_details")}:`}
                  </Typography>

                  <Typography
                    component="span"
                    fontSize={12.5}
                    textAlign="justify"
                    px={0.75}
                  >
                    {s.details}
                  </Typography>

                  {Array.isArray(s.todos) && s.todos.length > 0 && (
                    <Stack
                      my={0.75}
                      sx={{
                        borderTop: `1px solid ${theme.palette.blueGray50.main}`,
                      }}
                    >
                      <Typography
                        mt={0.5}
                        fontWeight={600}
                        component="span"
                        fontSize={12.5}
                      >
                        {`${t("activityPlan.assigned_tasks")}:`}
                      </Typography>
                      {s.todos.map((todo, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          component="span"
                          sx={{ ml: 2, mt: 0.5 }}
                        >
                          • {todo}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
              );
              const secondaryPlain = extractPlainText(secondaryText);

              return (
                <Collapse
                  key={i}
                  in={visible}
                  timeout={180}
                  mountOnEnter
                  unmountOnExit
                >
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        if (isAdded) return;
                        handleSelectSuggestion(s);
                      }}
                      dense
                      disabled={isAdded}
                      sx={{
                        border: `1px solid ${theme.palette.blueGray50.main}`,
                        backgroundColor: isAdded
                          ? alpha(theme.palette.success.main, 0.15)
                          : "transparent",
                        borderRadius: 3,
                        boxShadow: isAdded
                          ? ""
                          : `0 0 6px -4px ${theme.palette.common.black}`,
                      }}
                    >
                      <ListItemIcon>
                        {isAdded ? (
                          <CheckIconCircle color={theme.palette.primary.main} />
                        ) : (
                          <EGIconButton
                            iconName="add_square"
                            iconColor="success"
                            iconColorHover="white"
                            colorHover="success"
                            title={t("Interview.add")}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectSuggestion(s);
                            }}
                          />
                        )}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <TypingText
                            text={primaryText}
                            speed={2}
                            active={shouldAnimate && typingIndex === i}
                            onComplete={() => handlePrimaryComplete(i)}
                            startEmpty={shouldAnimate}
                          />
                        }
                        primaryTypographyProps={{
                          fontWeight: 600,
                          variant: "body1",
                          whiteSpace: "pre-line",
                          textAlign: "center",
                        }}
                        secondary={
                          shouldAnimate && typingIndex === i ? (
                            <TypingText
                              text={secondaryPlain}
                              speed={2}
                              active={shouldAnimate && typingIndex === i}
                              onComplete={() => handleSecondaryComplete(i)}
                              startEmpty={true}
                            />
                          ) : (
                            secondaryText
                          )
                        }
                        secondaryTypographyProps={{
                          component: "div",
                          variant: "body1",
                          fontSize: "0.83rem",
                          whiteSpace: "pre-line",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </Collapse>
              );
            })}
          </List>
        </Box>
      )}
    </BackdropAI>
  );
};
